import React, { ReactElement } from "react";

import styled, { css } from "styled-components";

import {
  CARD_FONT,
  CardProjectLine,
  EntityDots,
  StyledClamp,
  StyledTitleBlock,
  coverManagement,
  playControl,
  Placeholder,
} from "./shared";
import {
  ChecklistSmallIcon,
  DependencySmallIcon,
  EyeOffSmallIcon,
  MessageSmallIcon,
  PersonPlusIcon,
} from "../../../components/Icons";
import { SkeletonLoader } from "../../../components/Loaders";
import { Signifier } from "../../../components/Signifier";
import {
  STACKED_CARD_COVER_RATIO as COVER_RATIO,
  StackedCardCover as Cover,
  StackedCardRow as StackRow,
} from "../../../components/StackedCard";
import { Tag } from "../../../components/Tag";
import { Caption1 } from "../../../components/Typography";
import { Typography } from "../../../components/Typography/Typography";
import { RoundAvatar, AVATAR_COLORS } from "../../shared";

/**
 * TaskCard — the baseline-preservation card.
 *
 * This is the replacement for today's column card, so it must reproduce every
 * behavior that card has (note 52104 §B2) — none of them are optional, each is
 * a silent-regression risk. Every one is a prop here so the story can toggle it:
 *
 *  - task-number prefix in the title      (config-gated, mirrors `show_task_id`)
 *  - overdue date colouring               (red ONLY when overdue AND not completed)
 *  - dependency counter                   (behind a feature-flag prop)
 *  - hidden-from-clients signifier        (shown only when it DIFFERS from the
 *                                          project default)
 *  - inline assignee affordance           (avatar, or an add-assignee placeholder)
 *  - completed                            (teal tint, no "…" menu, no overdue colour)
 *  - assignees appear ONCE                (in the info line when the card has no
 *                                          date range, in the date row when it
 *                                          has one — never in both)
 *
 * The pending/optimistic and priority states are SHELL states (dimmed +
 * pointer-events off; red left bar), so the card is wrapped in a `<StackedCard
 * pending>` / `<StackedCard priorityColor>` by the host — they are not the
 * card's own concern and live on the shell, not here.
 *
 * Everything is measured against the app: `ColumnCardBody.jsx`,
 * `ColumnCardBodyInfo.jsx`, `ColumnCardDate.jsx` and
 * `view.project_tasks_column_mode.less`.
 */

export interface TaskCardLabel {
  text: string;
  /** The label's own colour — data, not theme. */
  color: string;
}

export interface TaskCardPerson {
  name: string;
  initials: string;
  /** Avatar colours stand in for the server-rendered avatar: data, not theme. */
  color: string;
}

/**
 * Eight labels, so the default card demonstrates the collapsed form. Colours are
 * the labels' own, which is why they are literals.
 */
const DEMO_LABELS: TaskCardLabel[] = [
  { text: "DESIGN", color: "#378ADD" },
  { text: "BLOCKED", color: "#D85A30" },
  { text: "READY", color: "#639922" },
  { text: "REVIEW", color: "#EF9F27" },
  { text: "RESEARCH", color: "#7F77DD" },
  { text: "COPY", color: "#8C5A2B" },
  { text: "QA", color: "#2F5DA8" },
  { text: "RELEASE", color: "#8A3E6B" },
];

/**
 * How many labels read as text before the line has to collapse them, and how
 * many dots stand in for the rest. Two colour-text `Tag`s fit a 260px card; past
 * that the app's own answer (and the design system's mapping) is coloured dots
 * plus an overflow count.
 */
const LABELS_AS_TEXT_MAX = 2;
const LABEL_DOTS_MAX = 5;

/* The completed tint fills the card body; the shell keeps the priority bar and
   selection ring on top of it. */
const StyledBody = styled.div<{ $completed?: boolean }>`
  display: flex;
  flex-direction: column;

  ${({ $completed }) =>
    $completed &&
    css`
      background: var(--sc-completed-tint, rgba(72, 201, 176, 0.16));
    `}
`;

/**
 * The info line — labels, then signifiers, then (only when the card has no date
 * range) the assignees.
 *
 * The separator is the app's, rule for rule: a 2px round dot in
 * `--color-theme-600` with `margin: 0 6px`, drawn by `::before` on every item
 * EXCEPT the first (`view.project_tasks_column_mode.less`:
 * `.c-signifier:not(:first-child):before`, `.label_single_container:not(:first-child)
 * .label_item:before`, `.dot:before`). Because the rule keys off position rather
 * than off "am I a signifier", a line that starts with a signifier — a task with
 * counters but no labels — cannot open with a dangling dot.
 *
 * `font-size: 11px` is the `.labels` block's own; the vertical rhythm the app
 * gets from `margin-top: 10px` comes from the `meta` row's role padding here.
 */
const StyledInfoLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  font-size: 11px;

  > * {
    display: flex;
    align-items: center;
    min-height: 18px;
  }

  /* The assignees are excluded: they end the line rather than continuing the
     enumeration, and the app draws no dot before them either. */
  > *:not(:first-child):not(.sc-assignees)::before {
    content: "";
    flex-shrink: 0;
    width: 2px;
    height: 2px;
    margin: 0 6px;
    border-radius: 100%;
    background-color: var(--color-theme-600);
  }

  /* Labels truncate rather than push the line wider: 74px for the item, 60px
     for the text inside it — the app's two numbers. */
  .sc-label {
    max-width: 74px;
  }

  .sc-label .c-tag > div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 60px;
  }

  .sc-dots {
    gap: 4px;
  }

  /* The assignees end the line, right-aligned, when there is no date row to
     carry them. */
  .sc-assignees {
    flex: 1 1 auto;
    justify-content: flex-end;
  }
`;

const StyledDot = styled.span<{ $color: string }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

/**
 * The date row's text.
 *
 * A raw `<span>` in this Storybook inherits the browser's serif default at the
 * ancestor's size, which is why the date used to read as huge serif type. The
 * app sets the same four values in CSS on a plain `div` (`.due_date`: 13px,
 * bold, 24px tall, `--color-theme-900`, `--red-alert` when `.overdue`), so the
 * fix is to state them here — plus the font stack the missing reset does not
 * supply.
 */
const StyledDate = styled.span<{ $overdue?: boolean }>`
  height: 24px;
  font-family: ${CARD_FONT};
  font-size: 13px;
  font-weight: bold;
  line-height: 24px;
  letter-spacing: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ $overdue }) =>
    $overdue ? "var(--red-alert)" : "var(--color-theme-900)"};
`;

/* Avatars are limited to 3 and overlap, as MultiAssigneesPicker does. */
const StyledAssignees = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

/* Inline assignee affordance placeholder — a dashed add-assignee target that
   stands in until the real picker is wired at integration. */
const StyledAddAssignee = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1.5px dashed var(--color-theme-400);
  border-radius: 50%;
  background: transparent;
  color: var(--color-theme-600);
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
    display: block;
  }

  &:hover {
    border-color: var(--sc-selection-ring);
    color: var(--color-theme-800);
  }
`;

/** The app's assignee limit (MultiAssigneesPicker `limit={3}`). */
const ASSIGNEE_LIMIT = 3;

export interface TaskCardProps {
  title?: string;
  /** Optional cover — task cards in a column usually have none. */
  cover?: boolean;
  playable?: boolean;
  coverControls?: boolean;
  /** Config-gated task-number prefix (mirrors `show_task_id`). */
  showTaskId?: boolean;
  taskId?: number;
  /**
   * The project the task lives in, above the title. Off by default: in a column
   * the column already says it, and only a surface that mixes projects (a
   * canvas) needs the line.
   */
  showProjectName?: boolean;
  projectName?: string;
  /**
   * The task's labels. Up to two read as colour text (`Tag`, the column card's
   * treatment); more collapse to coloured dots plus an overflow count, which is
   * what the design system maps ">2 labels" to. An empty array means the card
   * has no labels — and then the info line must not open with a separator.
   */
  labels?: TaskCardLabel[];
  /** Open-subtask and comment counters; each shows only when it is non-zero. */
  subtaskCount?: number;
  commentCount?: number;
  /**
   * The date range. `null` (or empty) means the task has none — the date row
   * disappears and the assignees move up into the info line, so they still
   * appear exactly once.
   */
  dateLabel?: string | null;
  /** Overdue date — colours the date red, but only when NOT completed. */
  overdue?: boolean;
  /** Dependency counter, gated behind a feature flag. */
  showDependencies?: boolean;
  dependencyCount?: number;
  /** The task's hidden-from-clients flag. */
  hiddenFromClients?: boolean;
  /** The project default for client visibility; the signifier shows on a diff. */
  projectDefaultHidden?: boolean;
  /** Whether the task has an assignee (avatar) or shows the add affordance. */
  assigned?: boolean;
  /** The actual assignees, when the host has them. Limited to 3, as in the app. */
  assignees?: TaskCardPerson[];
  /** Completed — teal tint, no "…" menu, no overdue colour. */
  completed?: boolean;
}

export const TaskCard = ({
  title = "Implement the design from the reference on this page",
  cover = false,
  playable = false,
  coverControls = false,
  showTaskId = false,
  taskId = 4521,
  showProjectName = false,
  projectName = "Marketing Site",
  labels = DEMO_LABELS,
  subtaskCount = 2,
  commentCount = 7,
  dateLabel = "May 8 – Sep 10",
  overdue = false,
  showDependencies = false,
  dependencyCount = 2,
  hiddenFromClients = false,
  projectDefaultHidden = false,
  assigned = true,
  assignees,
  completed = false,
}: TaskCardProps): ReactElement => {
  // Signifier appears only when the task's visibility differs from the default.
  const showHiddenSignifier = hiddenFromClients !== projectDefaultHidden;
  // Overdue colour is suppressed on completed tasks.
  const dateIsOverdue = overdue && !completed;
  const hasDate = dateLabel != null && dateLabel !== "";
  const collapseLabels = labels.length > LABELS_AS_TEXT_MAX;

  /* The assignee slot: the real avatars when the host has them, the demo avatar
     when it only says "assigned", the add affordance when it says nothing is. */
  const assigneeSlot = assigned ? (
    <StyledAssignees className="sc-assignees" data-fixed>
      {assignees ? (
        assignees.slice(0, ASSIGNEE_LIMIT).map((person) => (
          <RoundAvatar key={person.name} $bg={person.color} $size={24} $overlap>
            {person.initials}
          </RoundAvatar>
        ))
      ) : (
        <RoundAvatar $bg={AVATAR_COLORS.teal} $size={24} $bordered={false}>
          MS
        </RoundAvatar>
      )}
    </StyledAssignees>
  ) : (
    <StyledAssignees className="sc-assignees" data-fixed>
      <StyledAddAssignee type="button" aria-label="Assign task">
        <PersonPlusIcon />
      </StyledAddAssignee>
    </StyledAssignees>
  );

  /* Signifiers in the app's fixed order: subtasks → comments → dependencies →
     hidden-from-clients. Each is one child of the info line, so the line's own
     separator rule spaces them. */
  const signifiers: ReactElement[] = [];
  if (subtaskCount > 0) {
    signifiers.push(
      <Signifier
        key="subtasks"
        type={ChecklistSmallIcon}
        value={subtaskCount}
        tooltipText="Subtasks"
      />
    );
  }
  if (commentCount > 0) {
    signifiers.push(
      <Signifier
        key="comments"
        type={MessageSmallIcon}
        value={commentCount}
        tooltipText="Comments"
      />
    );
  }
  if (showDependencies && dependencyCount > 0) {
    signifiers.push(
      <Signifier
        key="dependencies"
        type={DependencySmallIcon}
        value={dependencyCount}
        tooltipText="Dependencies"
      />
    );
  }
  if (showHiddenSignifier) {
    signifiers.push(
      <Signifier
        key="hidden"
        type={EyeOffSmallIcon}
        tooltipText="Hidden from clients"
      />
    );
  }

  /* The info line exists when it has something to say — labels, signifiers, or
     the assignees it has to carry because there is no date row. */
  const showInfoLine = labels.length > 0 || signifiers.length > 0 || !hasDate;

  return (
    <StyledBody $completed={completed}>
      {cover ? (
        <Cover
          aspectRatio={COVER_RATIO}
          centerSlot={playable ? playControl : undefined}
          topRightSlot={coverControls ? coverManagement() : undefined}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "var(--color-theme-300)",
            }}
          />
        </Cover>
      ) : null}

      <StackRow role="title">
        <StyledTitleBlock>
          {showProjectName ? (
            <CardProjectLine>{projectName}</CardProjectLine>
          ) : null}
          <StyledClamp>
            {showTaskId ? (
              <Typography as="span" variant="Caption 1" color="tertiary">
                {`#${taskId} `}
              </Typography>
            ) : null}
            {title}
          </StyledClamp>
        </StyledTitleBlock>
        {/* Completed tasks drop the "…" menu. */}
        {completed ? null : <EntityDots aria-label="Task options" />}
      </StackRow>

      {showInfoLine ? (
        <StackRow role="meta">
          <StyledInfoLine>
            {collapseLabels ? (
              <span className="sc-dots">
                {labels.slice(0, LABEL_DOTS_MAX).map((label) => (
                  <StyledDot key={label.text} $color={label.color} />
                ))}
                {labels.length > LABEL_DOTS_MAX ? (
                  <Caption1 color="tertiary">{`+${
                    labels.length - LABEL_DOTS_MAX
                  }`}</Caption1>
                ) : null}
              </span>
            ) : (
              labels.map((label) => (
                <span className="sc-label" key={label.text}>
                  <Tag name={label.text} color={label.color} showDot={false} />
                </span>
              ))
            )}
            {signifiers}
            {/* Key invariant: assignees are here ONLY when the card has no date
                row to hold them. */}
            {hasDate ? null : assigneeSlot}
          </StyledInfoLine>
        </StackRow>
      ) : null}

      {hasDate ? (
        <StackRow role="footer">
          <StyledDate $overdue={dateIsOverdue}>{dateLabel}</StyledDate>
          {assigneeSlot}
        </StackRow>
      ) : null}
    </StyledBody>
  );
};

/* ---- placeholder + skeleton ---- */

export const TaskPlaceholder = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
): ReactElement => (
  <Placeholder
    icon={<ChecklistSmallIcon />}
    label="Name this task"
    {...props}
  />
);

/* Skeleton mirrors the task anatomy row for row: title, meta, footer. */
export const TaskSkeleton = (): ReactElement => (
  <>
    <StackRow role="title">
      <SkeletonLoader style={{ height: 14, width: "90%" }} />
    </StackRow>
    <StackRow role="meta">
      <SkeletonLoader style={{ height: 10, width: "55%" }} />
    </StackRow>
    <StackRow role="footer">
      <SkeletonLoader style={{ height: 12, width: 64 }} data-fixed />
      <span />
      <SkeletonLoader
        data-fixed
        style={{ width: 24, height: 24, borderRadius: "50%" }}
      />
    </StackRow>
  </>
);
